import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdClose } from "react-icons/io";
import { BsThreeDots } from "react-icons/bs";
import { HiOutlineBell } from "react-icons/hi2";
import { useNotificationStore } from "store";

const NotificationContainer = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: -0.5rem;
  background: white;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 380px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) !important;
    width: calc(100% - 2rem);
    max-width: 380px;
    height: auto;
    max-height: calc(100vh - 4rem);
    margin: 0 auto;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.25rem 1rem;
  position: sticky;
  top: 0;
  background: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  z-index: 1;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: #f3f4f6;
  }
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotificationItem = styled.div<{ read: boolean }>`
  display: flex;
  padding: 0.75rem 1.25rem;
  gap: 0.75rem;
  transition: background-color 0.2s;
  position: relative;
  cursor: pointer;

  &:hover {
    background: ${({ read }) => (read ? "#f3f4f6" : "#f9fafb")};
  }

  ${({ read }) =>
    read &&
    `
    opacity: 0.7;
    background: #f9fafb;
  `}
`;

const SystemIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: 24px;
`;

const Text = styled.p`
  margin: 0;
  font-size: 14px;
  color: #111827;
  line-height: 1.4;
  text-align: left;
`;

const Timestamp = styled.span`
  font-size: 12px;
  color: #6b7280;
  display: block;
  margin-top: 0.25rem;
  text-align: left;
`;

const ActionsButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
`;

const ActionDropdown = styled(motion.div)`
  position: absolute;
  right: 1.5rem;
  top: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 2;
  min-width: 120px;
`;

const ActionItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.5rem 1rem;
  background: none;
  border: none;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  font-family: "Manrope", sans-serif;

  &:hover {
    background: #f3f4f6;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  position: sticky;
  bottom: 0;
  background: white;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  z-index: 1;
`;

const FooterButton = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  font-family: "Manrope", sans-serif;

  &:hover {
    background: ${(props) =>
      props.color === "primary" ? "#f0f7ff" : "#f3f4f6"};
  }

  color: ${(props) => (props.color === "primary" ? "#5575f7" : "#6b7280")};
`;

const NotificationAction: React.FC<{
  notificationId: string;
  read: boolean;
}> = ({ notificationId, read }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { markAsRead } = useNotificationStore();
  const actionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionRef.current &&
        !actionRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead(notificationId);
    setIsOpen(false);
  };

  return (
    <div ref={actionRef} style={{ position: "relative" }}>
      <ActionsButton onClick={handleActionClick}>
        <BsThreeDots size={16} />
      </ActionsButton>
      <AnimatePresence>
        {isOpen && (
          <ActionDropdown
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ActionItem onClick={handleMarkAsRead}>Mark as read</ActionItem>
          </ActionDropdown>
        )}
      </AnimatePresence>
    </div>
  );
};

export const NotificationDropdown: React.FC = () => {
  const { notifications, isOpen, setIsOpen, markAllAsRead, markAsRead } =
    useNotificationStore();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <NotificationContainer
          ref={containerRef}
          initial={{ opacity: 0, y: -4, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.98 }}
          transition={{ duration: 0.15 }}
        >
          <Header>
            <Title>Notifications</Title>
            <CloseButton onClick={() => setIsOpen(false)}>
              <IoMdClose size={18} />
            </CloseButton>
          </Header>

          <NotificationList>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                read={notification.read}
                onClick={() =>
                  !notification.read && markAsRead(notification.id)
                }
              >
                <SystemIcon>
                  <HiOutlineBell size={18} />
                </SystemIcon>
                <Content>
                  <Text>{notification.text}</Text>
                  <Timestamp>{notification.timestamp}</Timestamp>
                </Content>
                <NotificationAction
                  notificationId={notification.id}
                  read={notification.read}
                />
              </NotificationItem>
            ))}
          </NotificationList>

          <Footer>
            <FooterButton onClick={() => setIsOpen(false)}>Close</FooterButton>
            <FooterButton color="primary" onClick={markAllAsRead}>
              Mark all as read
            </FooterButton>
          </Footer>
        </NotificationContainer>
      )}
    </AnimatePresence>
  );
};
